import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { myAuth } from "./firebaseProvider";
import {
  blockUser as blockUserRequest,
  getBlockIds,
  unblockUser as unblockUserRequest,
} from "../services/blockService";

import logger from "utils/logger";

/**
 * Estado de bloqueio compartilhado por toda a sessão.
 *
 * Existe por um motivo concreto: a lista de conversas chega ao app direto do
 * Firestore (onSnapshot), sem passar pela API — então o filtro de "quem eu
 * bloqueei" e a marca de "quem me bloqueou" precisam ser resolvidos no
 * cliente. Carregar os dois conjuntos uma vez e mantê-los em memória evita
 * uma chamada por conversa e por perfil aberto.
 *
 * As ações são otimistas: bloquear tira a pessoa da tela na hora e desfaz o
 * estado local se a API recusar.
 */

const BlockContext = createContext(null);

const emptyState = { blockedByMe: [], blockedMe: [] };

export const BlockProvider = ({ children }) => {
  const [state, setState] = useState(emptyState);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!myAuth.currentUser) {
      setState(emptyState);
      setIsLoading(false);
      return;
    }
    try {
      const { data } = await getBlockIds();
      setState({
        blockedByMe: data?.blocked_by_me || [],
        blockedMe: data?.blocked_me || [],
      });
    } catch (error) {
      // Falha aqui não pode derrubar a navegação: o backend continua barrando
      // conversa e match de qualquer forma; só o realce visual fica ausente.
      logger.warn("blockProvider: falha ao carregar bloqueios", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = myAuth.onAuthStateChanged(() => refresh());
    return () => unsubscribe();
  }, [refresh]);

  const blockUser = useCallback(async (userId) => {
    setState((prev) =>
      prev.blockedByMe.includes(userId)
        ? prev
        : { ...prev, blockedByMe: [...prev.blockedByMe, userId] },
    );
    try {
      await blockUserRequest(userId);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        blockedByMe: prev.blockedByMe.filter((id) => id !== userId),
      }));
      throw error;
    }
  }, []);

  const unblockUser = useCallback(async (userId) => {
    setState((prev) => ({
      ...prev,
      blockedByMe: prev.blockedByMe.filter((id) => id !== userId),
    }));
    try {
      await unblockUserRequest(userId);
    } catch (error) {
      setState((prev) =>
        prev.blockedByMe.includes(userId)
          ? prev
          : { ...prev, blockedByMe: [...prev.blockedByMe, userId] },
      );
      throw error;
    }
  }, []);

  const value = useMemo(() => {
    const blockedByMe = new Set(state.blockedByMe);
    const blockedMe = new Set(state.blockedMe);

    return {
      isLoading,
      refresh,
      blockUser,
      unblockUser,
      blockedByMeIds: state.blockedByMe,
      /** Eu bloqueei esta pessoa. */
      isBlockedByMe: (userId) => Boolean(userId) && blockedByMe.has(userId),
      /** Esta pessoa me bloqueou. */
      hasBlockedMe: (userId) => Boolean(userId) && blockedMe.has(userId),
      /** Há bloqueio em qualquer direção — nenhuma interação é possível. */
      isBlocked: (userId) =>
        Boolean(userId) && (blockedByMe.has(userId) || blockedMe.has(userId)),
    };
  }, [state, isLoading, refresh, blockUser, unblockUser]);

  return (
    <BlockContext.Provider value={value}>{children}</BlockContext.Provider>
  );
};

/**
 * Fora do provider (testes de unidade de componentes isolados, por exemplo)
 * devolve um estado neutro em vez de quebrar a árvore.
 */
const fallback = {
  isLoading: false,
  refresh: async () => {},
  blockUser: async () => {},
  unblockUser: async () => {},
  blockedByMeIds: [],
  isBlockedByMe: () => false,
  hasBlockedMe: () => false,
  isBlocked: () => false,
};

export const useBlocks = () => useContext(BlockContext) || fallback;

export default BlockProvider;
